import {
    coverageAppliesToModel,
    documentLinkFit,
    isAppVisibleDocument,
    isDirectLink,
    isMisfit,
    isSupersededEntry,
    type ModelDocumentEntry,
} from '@rock/manuals';
import type { BrandId, EquipmentTypeFamily, EquipmentTypeId, Model } from '@rock/types';

/** A change to a model's identity that its documents may not follow: a new type, or a new (or no) brand. */
export type ModelIdentityChange =
    | { kind: 'type'; typeId: EquipmentTypeId }
    | { kind: 'brand'; brandId: BrandId | null; lineId?: string | null };

/** The documents a model change affects, split by how they are linked. */
export interface ModelChangeEffect {
    /** Linked directly, fitting the model today, and not after: the admin chooses to move each along or unlink it. */
    direct: ModelDocumentEntry[];
    /** Linked by rule, and the rule stops reaching the model: the reconcile trigger unlinks these. */
    leaving: ModelDocumentEntry[];
}

/** What a type or brand change does to the model's linked documents, by the rule the server applies. */
export function modelChangeEffect(
    entries: ModelDocumentEntry[],
    model: Model,
    change: ModelIdentityChange,
    families: readonly EquipmentTypeFamily[],
): ModelChangeEffect {
    const next = applyIdentityChange(model, change);
    return {
        direct: entries.filter(
            (entry) =>
                isDirectLink(entry.link) &&
                !isMisfit(documentLinkFit(entry.doc, model, families)) &&
                isMisfit(documentLinkFit(entry.doc, next, families)),
        ),
        leaving: entries.filter(
            (entry) => !isDirectLink(entry.link) && !coverageAppliesToModel(entry.doc, next, families),
        ),
    };
}

/** Whether the change touches any document at all — nothing to ask about otherwise. */
export function hasModelChangeEffect(effect: ModelChangeEffect): boolean {
    return effect.direct.length + effect.leaving.length > 0;
}

/** Whether the model loses a document the app shows, so searching again is worth offering by default. */
export function losesServedDocument(effect: ModelChangeEffect, unlinked: ModelDocumentEntry[]): boolean {
    return [...effect.leaving, ...unlinked].some(
        (entry) => isAppVisibleDocument(entry.doc) && !isSupersededEntry(entry),
    );
}

/** The model as it would read after the change. */
function applyIdentityChange(model: Model, change: ModelIdentityChange): Model {
    if (change.kind === 'type') return { ...model, typeId: change.typeId };
    const { brandId: _old, ...rest } = model;
    return change.brandId ? { ...rest, brandId: change.brandId } : rest;
}
